import React from "react";
import {
  AbsoluteFill,
  Audio,
  Composition,
  Easing,
  Img,
  interpolate,
  Sequence,
  staticFile,
  useCurrentFrame,
} from "remotion";

export const JackRobertsComposition: React.FC = () => {
  return (
    <Composition
      id="JackRobertsStyle"
      component={JackRobertsScene}
      durationInFrames={200} // 6.66 seconds @ 30fps (matching voice_natural.mp3)
      fps={30}
      width={1080}
      height={1920}
    />
  );
};

export const JackRobertsScene: React.FC = () => {
  const frame = useCurrentFrame();

  // Signature Jack Roberts Palette
  const BEIGE_GROUND = "#e1d8ca"; // Flat beige ground, zero cheesy gradient
  const TEXT_DARK = "#1f1d1a";

  // Signature Speed Ramp Scale: enters fast (1.12), settles smoothly to 0.88
  const groupScale = interpolate(frame, [0, 32], [1.12, 0.88], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
  });

  // Mascot sprite frame calculation (47 frames sequence)
  const mascotFrame = Math.floor(frame * 0.9) % 47;
  const numStr = mascotFrame < 10 ? `0${mascotFrame}` : `${mascotFrame}`;
  const mascotPng = `mascots/laptop/f${numStr}.png`;

  // Terminal typing simulation
  const commandText = 'claude "bikin motion graphics remotion 9:16"';
  const charsTyped = Math.min(
    commandText.length,
    Math.max(0, Math.floor((frame - 15) * 1.6))
  );
  const currentTyped = commandText.slice(0, charsTyped);

  // Staggered output lines appearing
  const showOutput1 = frame >= 45;
  const showOutput2 = frame >= 75;
  const showOutput3 = frame >= 105;
  const showOutput4 = frame >= 135;

  // Kinetic Caption sync
  // 0-3.2s: "Sekarang kamu bisa bikin animasi motion graphics sekelas After Effects..."
  // 3.2-6.6s: "...cuma dari prompt teks di terminal Claude Code."
  const isSecondPart = frame >= 96;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: BEIGE_GROUND,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: TEXT_DARK,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "100px 50px 90px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* SFX & Natural Edge-TTS Audio */}
      <Audio src={staticFile("voice_natural.mp3")} />
      <Audio src={staticFile("whoosh.mp3")} volume={0.45} />
      <Sequence from={20} layout="none">
        <Audio src={staticFile("click.mp3")} volume={0.65} />
      </Sequence>
      <Sequence from={50} layout="none">
        <Audio src={staticFile("click.mp3")} volume={0.4} />
      </Sequence>

      {/* TOP: Crisp Minimal Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          zIndex: 10,
        }}
      >
        <span
          style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: "#6b6255",
          }}
        >
          Anthropic × Remotion
        </span>
      </div>

      {/* CENTER: THE ORANGE SCENE (Mascot standing on Real Terminal) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 20,
          transform: `scale(${groupScale})`,
          transformOrigin: "center 65%",
          filter: "drop-shadow(0 30px 60px rgba(50, 40, 30, 0.22))",
        }}
      >
        {/* Animated Clawd Mascot Typing on Laptop */}
        <div
          style={{
            position: "relative",
            width: 240,
            height: 162,
            marginBottom: -22, // Tucks feet cleanly behind terminal title bar
            zIndex: 15,
          }}
        >
          <Img
            src={staticFile(mascotPng)}
            style={{
              width: "100%",
              height: "100%",
              imageRendering: "pixelated",
              objectFit: "contain",
            }}
          />
        </div>

        {/* macOS Terminal Window */}
        <div
          style={{
            width: 900,
            backgroundColor: "#1c1a1f",
            borderRadius: 22,
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow:
              "0 20px 50px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.12)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* macOS Title Bar */}
          <div
            style={{
              height: 52,
              backgroundColor: "#2a272e",
              borderBottom: "1px solid rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              padding: "0 22px",
              position: "relative",
            }}
          >
            {/* Traffic light window controls */}
            <div style={{ display: "flex", gap: 9 }}>
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#ff5f56",
                }}
              />
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#ffbd2e",
                }}
              />
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#27c93f",
                }}
              />
            </div>

            {/* Title */}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                fontSize: 16,
                fontWeight: 600,
                color: "#9e9aa4",
                fontFamily: "ui-monospace, 'SF Mono', monospace",
              }}
            >
              claude-code — remotion-studio
            </div>
          </div>

          {/* Terminal Body */}
          <div
            style={{
              padding: "32px 36px 40px",
              fontFamily: "ui-monospace, 'SF Mono', 'Menlo', monospace",
              fontSize: 23,
              lineHeight: 1.6,
              color: "#ecebf0",
              minHeight: 330,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* Command Line */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ color: "#d97757", fontWeight: 700 }}>❯</span>
              <span style={{ color: "#ffffff", fontWeight: 600 }}>
                {currentTyped}
              </span>
              {frame < 45 && (
                <span
                  style={{
                    display: "inline-block",
                    width: 11,
                    height: 24,
                    backgroundColor: "#d97757",
                    marginLeft: 2,
                    opacity: Math.floor(frame / 6) % 2 === 0 ? 1 : 0,
                  }}
                />
              )}
            </div>

            {/* Output lines appearing with speed */}
            {showOutput1 && (
              <div style={{ color: "#8a8594", marginTop: 8 }}>
                [remotion] Initializing 1080x1920 vertical composition...
              </div>
            )}
            {showOutput2 && (
              <div style={{ color: "#38bdf8" }}>
                ✔ Loaded 26 motion graphic templates + spring physics
              </div>
            )}
            {showOutput3 && (
              <div style={{ color: "#22c55e", fontWeight: 600 }}>
                ✔ Rendered 200 frames @ 30fps (H.264 + AAC Audio)
              </div>
            )}
            {showOutput4 && (
              <div
                style={{
                  color: "#d97757",
                  fontWeight: 700,
                  background: "rgba(217, 119, 87, 0.12)",
                  padding: "8px 14px",
                  borderRadius: 8,
                  alignSelf: "flex-start",
                  marginTop: 6,
                }}
              >
                ★ Ready: out/motion_graphics.mp4
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BOTTOM: Minimal Kinetic Caption Card (Editor Style) */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          zIndex: 30,
        }}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 24,
            padding: "24px 38px",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 15px 35px rgba(50,40,30,0.12)",
            textAlign: "center",
            maxWidth: 880,
          }}
        >
          <div
            style={{
              fontSize: 32,
              fontWeight: 800,
              lineHeight: 1.35,
              color: TEXT_DARK,
              letterSpacing: -0.5,
            }}
          >
            {!isSecondPart ? (
              <span>
                Bikin animasi motion graphics{" "}
                <span
                  style={{
                    backgroundColor: "#f5e8d8",
                    color: "#b45309",
                    padding: "2px 10px",
                    borderRadius: 8,
                  }}
                >
                  sekelas After Effects
                </span>
              </span>
            ) : (
              <span>
                Cuma dari{" "}
                <span
                  style={{
                    backgroundColor: "#fbe8e2",
                    color: "#c2410c",
                    padding: "2px 10px",
                    borderRadius: 8,
                  }}
                >
                  prompt teks
                </span>{" "}
                di terminal Claude Code.
              </span>
            )}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
